import { takeLatest, put, call } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { history } from '@utils/BrowserHistory';
import { resourceService } from '@libs/axios';
import { ResponseType } from '@store/index';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    fetchingPartnerList,
    fetchingPartnerOptions,
    createPartner,
    getPartnerDetail,
    updatePartner,
    clearPartner,
    UpdatePartnerPayload,
    fetchingSyncRequestList,
    getSyncRequestDetail,
    syncDataFromPartner,
    syncDataPartnerById,
    deleteSyncRequests,
} from '@store/actions/partnerActions';
import {
    setPartnerList,
    setPartnerOptions,
    setPartnerCreation,
    clearPartnerData,
    setLoading,
    setSuccess,
    setSyncRequestList,
    setSyncRequestDetail,
    setLoadingSyncRequest,
    setSyncDataResult,
    setLoadingSyncData,
    setSyncByIdResult,
    setLoadingSyncById,
    setDeleteRequestsResult,
    setLoadingDeleteRequests,
} from '@store/reducers/partnerReducer';

const API_URL = process.env.REACT_APP_API_URL;

const ERROR_TRANSLATIONS: Record<string, string> = {
    'provinceCode and wardCode are required in school': 'Mã tỉnh/thành phố và phường/xã là bắt buộc trong trường học, tạm thời chưa thể đồng bộ',
    'provinceCode is required in school': 'Mã tỉnh/thành phố là bắt buộc trong trường học, tạm thời chưa thể đồng bộ',
    'wardCode is required in school': 'Mã phường/xã là bắt buộc trong trường học, tạm thời chưa thể đồng bộ',
};

const translateError = (msg: string): string => ERROR_TRANSLATIONS[msg] ?? msg;

function* fetchPartnerListData(action: ReturnType<typeof fetchingPartnerList>): Generator<any, void, any> {
    const { code_name, status, page = 1, page_size = 10 } = action.payload || {};
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.get, `${API_URL}/api/partners/`, {
            params: { code_name, status, page, page_size },
        });
        if (response?.data) {
            yield put(setPartnerList(response.data));
        }
    } catch (e) {
        console.log(e);
    } finally {
        yield put(setLoading(false));
    }
}

function* fetchPartnerOptionsSaga(): Generator<any, void, any> {
    try {
        const response: ResponseType = yield call(resourceService.get, `${API_URL}/api/partners/`, {
            params: { status: 1, page: 1, page_size: 500 },
        });
        if (response?.data?.results) {
            yield put(setPartnerOptions(response.data.results));
        }
    } catch (e) {
        console.log(e);
    }
}

function* createPartnerSaga(action: ReturnType<typeof createPartner>): Generator<any, void, any> {
    yield put(setSuccess(false));
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.post, `${API_URL}/api/partners/`, action.payload);
        if (response.status === 201) {
            toast.success('Tạo đối tác mới thành công');
            yield put(setPartnerCreation(response.data));
            yield put(setSuccess(true));
        }
    } catch (error: any) {
        toast.error(error?.response?.data?.code?.[0] || 'Tạo đối tác mới thất bại');
        yield put(setSuccess(false));
    } finally {
        yield put(setLoading(false));
    }
}

function* getPartnerDetailSaga(action: ReturnType<typeof getPartnerDetail>): Generator<any, void, any> {
    const { id } = action.payload;
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.get, `${API_URL}/api/partners/${id}/`);
        if (response?.data) {
            yield put(setPartnerCreation({
                name: response.data.name,
                code: response.data.code,
                status: response.data.status,
            }));
        }
    } catch (e) {
        console.log(e);
    } finally {
        yield put(setLoading(false));
    }
}

function* updatePartnerSaga(action: PayloadAction<UpdatePartnerPayload>): Generator<any, void, any> {
    const { data, id } = action.payload;
    yield put(setSuccess(false));
    yield put(setLoading(true));
    try {
        const response: ResponseType = yield call(resourceService.put, `${API_URL}/api/partners/${id}/`, data);
        if (response.status === 200) {
            toast.success('Chỉnh sửa thông tin đối tác thành công');
            yield put(setPartnerCreation(response.data));
            yield put(setSuccess(true));
        }
    } catch (error: any) {
        toast.error(error?.response?.data?.code?.[0] || 'Chỉnh sửa thông tin đối tác thất bại');
        yield put(setSuccess(false));
    } finally {
        yield put(setLoading(false));
    }
}

function* clearPartnerSaga(): Generator<any, void, any> {
    yield put(clearPartnerData());
}

function* fetchSyncRequestListSaga(action: ReturnType<typeof fetchingSyncRequestList>): Generator<any, void, any> {
    const { request_type, request_code, status, page = 1, page_size = 10, partner } = action.payload || {};
    yield put(setLoadingSyncRequest(true));
    try {
        const params: any = { page, page_size };
        if (request_type) params.request_type = request_type;
        if (request_code) params.request_code = request_code;
        if (status !== null && status !== undefined) params.status = status;
        if (partner) params.partner = partner;

        const response: ResponseType = yield call(
            resourceService.get,
            `${API_URL}/api/request_sync_data/`,
            { params },
        );
        if (response?.data) {
            yield put(setSyncRequestList(response.data));
        }
    } catch (e) {
        console.log(e);
    } finally {
        yield put(setLoadingSyncRequest(false));
    }
}

function* getSyncRequestDetailSaga(action: ReturnType<typeof getSyncRequestDetail>): Generator<any, void, any> {
    const { id } = action.payload;
    yield put(setLoadingSyncRequest(true));
    try {
        const response: ResponseType = yield call(
            resourceService.get,
            `${API_URL}/api/request_sync_data/${id}/`,
        );
        if (response?.data) {
            yield put(setSyncRequestDetail(response.data));
        }
    } catch (e) {
        console.log(e);
    } finally {
        yield put(setLoadingSyncRequest(false));
    }
}

function* syncDataFromPartnerSaga(action: ReturnType<typeof syncDataFromPartner>): Generator<any, void, any> {
    const { from_date, to_date, partnerId, listFilters } = action.payload;
    yield put(setLoadingSyncData(true));
    yield put(setSyncDataResult(null));
    try {
        const response: ResponseType = yield call(
            resourceService.get,
            `${API_URL}/api/sync_data_from_partner/`,
            { params: { from_date, to_date } },
        );
        if (response?.data) {
            yield put(setSyncDataResult(response.data));
            const { summary } = response.data;
            if (summary?.failed > 0) {
                toast.warning(`Đồng bộ hoàn tất: ${summary.failed} mục thất bại`);
            } else {
                toast.success('Đồng bộ dữ liệu thành công');
            }
        }
    } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Đồng bộ dữ liệu thất bại');
    } finally {
        yield put(setLoadingSyncData(false));
        // Luôn reload list dù thành công hay lỗi
        if (partnerId) {
            yield put(fetchingSyncRequestList({
                ...(listFilters ?? {}),
                partner: partnerId,
            }));
        }
    }
}

function* syncDataPartnerByIdSaga(action: ReturnType<typeof syncDataPartnerById>): Generator<any, void, any> {
    const { id, partnerId, listFilters } = action.payload;
    yield put(setLoadingSyncById(true));
    yield put(setSyncByIdResult(null));
    try {
        const response: ResponseType = yield call(
            resourceService.get,
            `${API_URL}/api/sync_data_partner/${id}/`,
        );

        const data = response?.data;

        if (data?.success === false) {
            const errors: string[] = data?.errors ?? [];
            if (errors.length > 0) {
                errors.forEach((err: string) => toast.error(translateError(err)));
            } else {
                toast.error('Đồng bộ yêu cầu thất bại');
            }
            return;
        }

        if (data) {
            yield put(setSyncByIdResult(data));
            if (data.status === 3) {
                toast.success('Đồng bộ yêu cầu thành công');
            } else if (data.status === 4) {
                toast.error('Đồng bộ yêu cầu thất bại');
            } else {
                toast.info('Đã gửi yêu cầu đồng bộ');
            }
        }
    } catch (e: any) {
        const errData = e?.response?.data;
        if (errData?.errors && Array.isArray(errData.errors) && errData.errors.length > 0) {
            errData.errors.forEach((err: string) => toast.error(translateError(err)));
        } else {
            toast.error(errData?.message ? translateError(errData.message) : 'Đồng bộ yêu cầu thất bại');
        }
    } finally {
        yield put(setLoadingSyncById(false));
        // Luôn reload list dù thành công hay lỗi
        if (partnerId) {
            yield put(fetchingSyncRequestList({
                ...(listFilters ?? {}),
                partner: partnerId,
            }));
        }
    }
}

function* deleteSyncRequestsSaga(action: ReturnType<typeof deleteSyncRequests>): Generator<any, void, any> {
    const { ids, partnerId, listFilters } = action.payload;
    yield put(setLoadingDeleteRequests(true));
    yield put(setDeleteRequestsResult(null));
    try {
        const response: ResponseType = yield call(
            resourceService.post,
            `${API_URL}/api/request_sync_data/delete-request/`,
            { request_ids: ids },
        );
        if (response?.data) {
            yield put(setDeleteRequestsResult(response.data));
            const { deleted_count, missing_ids } = response.data;
            if (missing_ids?.length > 0) {
                toast.warning(`Đã xóa ${deleted_count} yêu cầu. ${missing_ids.length} yêu cầu không tìm thấy.`);
            } else {
                toast.success(`Đã xóa ${deleted_count} yêu cầu thành công`);
            }
        }
    } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Xóa yêu cầu thất bại');
    } finally {
        yield put(setLoadingDeleteRequests(false));
        if (partnerId) {
            yield put(fetchingSyncRequestList({
                ...(listFilters ?? {}),
                partner: partnerId,
            }));
        }
    }
}

export default function* partnerSaga() {
    yield takeLatest(fetchingPartnerList, fetchPartnerListData);
    yield takeLatest(fetchingPartnerOptions, fetchPartnerOptionsSaga);
    yield takeLatest(createPartner, createPartnerSaga);
    yield takeLatest(getPartnerDetail, getPartnerDetailSaga);
    yield takeLatest(updatePartner, updatePartnerSaga);
    yield takeLatest(clearPartner, clearPartnerSaga);
    yield takeLatest(fetchingSyncRequestList, fetchSyncRequestListSaga);
    yield takeLatest(getSyncRequestDetail, getSyncRequestDetailSaga);
    yield takeLatest(syncDataFromPartner, syncDataFromPartnerSaga);
    yield takeLatest(syncDataPartnerById, syncDataPartnerByIdSaga);
    yield takeLatest(deleteSyncRequests, deleteSyncRequestsSaga);
}
